import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Grid,
  CircularProgress,
  TableBody,
  TableHead,
  TableRow,
  Autocomplete,
  TextField,
  Tooltip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import { useGetCustomerOutstandingReport } from 'src/services/report.service';
import { getBasicCustomers } from 'src/services/customer.service';
import { generateCustomerOutstandingReportCSV } from 'src/utils/csv.utils';
import { generateCustomerOutstandingReportPDF } from 'src/components/Invoice/CustomerOutstandingReportPDF';
import MainLayout from 'src/pages/private';
import {
  ReportPageWrapper,
  FilterPaper,
  FilterActionsBox,
  FlexSpacer,
  GenerateButton,
  PdfIconButton,
  CsvIconButton,
  LoadingBox,
  SummaryGrid,
  StatCard,
  StatLabel,
  StatValue,
  StatValueSuccess,
  StatValueError,
  StyledTable,
  StyledTableContainer,
  BorderlessRow,
  HeaderCell,
  DataCell,
  DataCellError,
  EmptyCell,
} from './ReportPage.styled';

const CustomerOutstandingReport: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Custom hook for this specific report
  const { loading, error, reportData, fetchCustomerOutstandingReport } =
    useGetCustomerOutstandingReport();

  const [customers, setCustomers] = useState<{ id: string; name: string }[]>(
    []
  );
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('all');
  const [selectedCustomerName, setSelectedCustomerName] = useState<
    string | null
  >(null);

  useEffect(() => {
    // Fetch customers for dropdown
    const fetchCustomersList = async () => {
      try {
        const data = await getBasicCustomers();
        setCustomers(data);
      } catch (err) {
        console.error('Failed to load customers', err);
      }
    };
    fetchCustomersList();
  }, []);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  const handleGenerateReport = useCallback(() => {
    fetchCustomerOutstandingReport({
      customerId: selectedCustomerId === 'all' ? undefined : selectedCustomerId,
    });
  }, [selectedCustomerId, fetchCustomerOutstandingReport]);

  // Run once on mount — fetchCustomerOutstandingReport is stable (useCallback with [])
  useEffect(() => {
    fetchCustomerOutstandingReport({});
  }, []);

  const handleExportCSV = () => {
    if (reportData) {
      generateCustomerOutstandingReportCSV(reportData, {
        customerName: selectedCustomerName,
      });
    }
  };

  const handleExportPDF = async () => {
    if (reportData) {
      try {
        await generateCustomerOutstandingReportPDF(reportData, {
          customerName: selectedCustomerName,
        });
      } catch (err) {
        console.error('Error generating PDF:', err);
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  return (
    <MainLayout title="Customer Outstanding Report">
      <ReportPageWrapper>
        <FilterPaper elevation={0}>
          <Grid container spacing={3} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={[{ id: 'all', name: 'All Customers' }, ...customers]}
                getOptionLabel={(option) => option.name}
                value={
                  selectedCustomerId === 'all'
                    ? { id: 'all', name: 'All Customers' }
                    : customers.find((c) => c.id === selectedCustomerId) || null
                }
                onChange={(event, newValue) => {
                  setSelectedCustomerId(newValue ? newValue.id : 'all');
                  setSelectedCustomerName(
                    newValue && newValue.id !== 'all' ? newValue.name : null
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Customer"
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <FilterActionsBox>
                <GenerateButton
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleGenerateReport}
                  disabled={loading}
                >
                  Generate
                </GenerateButton>
                <FlexSpacer />
                <Tooltip title="Export to PDF">
                  <span>
                    <PdfIconButton
                      onClick={handleExportPDF}
                      disabled={loading || !reportData}
                    >
                      <PictureAsPdfIcon fontSize="small" />
                    </PdfIconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Export to CSV">
                  <span>
                    <CsvIconButton
                      onClick={handleExportCSV}
                      disabled={loading || !reportData}
                    >
                      <DescriptionIcon fontSize="small" />
                    </CsvIconButton>
                  </span>
                </Tooltip>
              </FilterActionsBox>
            </Grid>
          </Grid>
        </FilterPaper>

        {loading ? (
          <LoadingBox>
            <CircularProgress />
          </LoadingBox>
        ) : (
          <>
            <SummaryGrid container spacing={3}>
              <Grid item xs={12} md={4}>
                <StatCard elevation={0}>
                  <StatLabel variant="subtitle2" color="textSecondary">
                    Grand Total
                  </StatLabel>
                  <StatValue variant="h4">
                    {formatCurrency(reportData?.summary?.totalGrandTotal || 0)}
                  </StatValue>
                </StatCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <StatCard elevation={0}>
                  <StatLabel variant="subtitle2" color="textSecondary">
                    Paid Amount
                  </StatLabel>
                  <StatValueSuccess variant="h4">
                    {formatCurrency(reportData?.summary?.totalPaidAmount || 0)}
                  </StatValueSuccess>
                </StatCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <StatCard elevation={0}>
                  <StatLabel variant="subtitle2" color="textSecondary">
                    Total Outstanding Amount
                  </StatLabel>
                  <StatValueError variant="h4">
                    {formatCurrency(reportData?.summary?.totalOutstanding || 0)}
                  </StatValueError>
                </StatCard>
              </Grid>
            </SummaryGrid>

            <StyledTableContainer>
              <StyledTable>
                <TableHead>
                  <TableRow>
                    <HeaderCell>Customer Name</HeaderCell>
                    <HeaderCell align="right">Grand Total</HeaderCell>
                    <HeaderCell align="right">Paid Amount</HeaderCell>
                    <HeaderCell align="right">Outstanding Amount</HeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData &&
                  reportData.customers &&
                  reportData.customers.length > 0 ? (
                    reportData.customers.map((row) => (
                      <BorderlessRow key={row.customerId}>
                        <DataCell component="th" scope="row">
                          {row.customerName}
                        </DataCell>
                        <DataCell align="right">
                          {formatCurrency(row.grandTotal)}
                        </DataCell>
                        <DataCell align="right">
                          {formatCurrency(row.paidAmount)}
                        </DataCell>
                        <DataCellError align="right">
                          {formatCurrency(row.balanceAmount)}
                        </DataCellError>
                      </BorderlessRow>
                    ))
                  ) : (
                    <TableRow>
                      <EmptyCell colSpan={4} align="center">
                        <Typography variant="body1" color="textSecondary">
                          No outstanding balances found for the selected
                          criteria.
                        </Typography>
                      </EmptyCell>
                    </TableRow>
                  )}
                </TableBody>
              </StyledTable>
            </StyledTableContainer>
          </>
        )}
      </ReportPageWrapper>
    </MainLayout>
  );
};

export default CustomerOutstandingReport;
